import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { UrlStateService } from '../../url-state/url-state.service';
import { UrlParamDefinition } from '../../url-state/url-state.types';
import { PaginationChange } from '../pagination/pagination.component';
import { DataTableColumn, DataTableOptions, DataTableRequest, DataTableResponse, SortDirection } from './data-table.types';

@Component({
  selector: 'ss-data-table',
  templateUrl: './data-table.component.html',
  standalone: false
})
export class DataTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() options!: DataTableOptions;
  @Input() trigger?: Observable<any>;
  @Output() loaded = new EventEmitter<DataTableResponse>();
  @Output() rowClick = new EventEmitter<any>();

  rows: any[] = [];
  total = 0;
  page = 1;
  pageSize = 15;
  loading = false;
  order: [number, SortDirection][] = [];

  private expandedKeys = new Set<string>();
  private draw = 0;
  private started = false;
  private loadVersion = 0;
  private destroyed = false;
  private triggerSubscription?: Subscription;

  constructor(private urlState: UrlStateService) { }

  get columns(): DataTableColumn[] {
    return (this.options?.columns || []).filter((column, index) =>
      column.visible !== false && this.defsFor(index).every((def) => def['visible'] !== false));
  }

  columnClass(column: DataTableColumn): string {
    const index = (this.options?.columns || []).indexOf(column);
    return [...this.defsFor(index).map((def) => def.className), column.className].filter(Boolean).join(' ');
  }

  private defsFor(index: number) {
    return (this.options?.columnDefs || []).filter(({ targets }) =>
      targets === '_all' || targets === index || (Array.isArray(targets) && targets.includes(index)));
  }

  private sortColumnIndex(index: number): number {
    const def = this.defsFor(index).find((item) => item['orderData'] !== undefined);
    const target = def ? def['orderData'] : (this.options.columns[index] as any)?.orderData;
    return target === undefined ? index : (Array.isArray(target) ? target[0] : target);
  }

  get expandable(): boolean {
    return typeof this.options?.rowDetail === 'function';
  }

  private rowKey(row: any): string {
    return String(this.valueOf(row, this.options?.rowKey || 'id') ?? this.rows.indexOf(row));
  }

  isExpanded(row: any): boolean {
    return this.expandedKeys.has(this.rowKey(row));
  }

  onRowClick(event: Event, row: any) {
    const target = event.target as Element | null;
    if (target?.closest('a, button, input, select, textarea, label')) return;
    this.rowClick.emit(row);
  }

  toggleRow(row: any) {
    const key = this.rowKey(row);
    if (this.expandedKeys.has(key)) this.expandedKeys.delete(key);
    else this.expandedKeys.add(key);
  }

  detailId(row: any, rowIndex: number): string {
    return `ss-row-detail-${this.rowKey(row) || rowIndex}`.replace(/[^\w-]/g, '');
  }

  expandLabel(row: any): string {
    const base = this.options?.rowDetailLabel ? this.options.rowDetailLabel(row) : 'detalhes da linha';
    return `${this.isExpanded(row) ? 'Recolher' : 'Expandir'} ${base}`;
  }

  get paging(): boolean {
    return this.options?.paging !== false;
  }

  get pageSizeOptions(): number[] {
    const menu = this.options?.lengthMenu;
    const sizes = Array.isArray(menu?.[0]) ? (menu![0] as number[]) : (menu as number[] | undefined);
    return (sizes || [15, 50, 100]).filter((size) => size > 0);
  }

  ngOnInit() {
    this.pageSize = this.options?.pageLength || 15;
    this.order = this.options?.ordering === false ? [] : this.options?.order ? [...this.options.order] : [[0, 'asc']];

    this.restoreUrlState();

    if (this.trigger) {

      this.triggerSubscription = this.trigger.subscribe(() => Promise.resolve().then(() => this.load()));
    } else {
      this.load();
    }
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes['options'] && !changes['options'].firstChange && this.started && !this.isServerSide()) {
      this.load();
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.loadVersion++;
    this.triggerSubscription?.unsubscribe();
  }

  reload(resetPage = true) {
    if (resetPage) this.page = 1;
    this.syncUrlState();
    this.load();
  }

  setPageSize(size: number) {
    this.pageSize = Number(size) || this.pageSize;
    this.page = 1;
    this.syncUrlState();
    this.load();
  }

  changePage(change: PaginationChange) {
    this.page = change.page;
    this.pageSize = change.pageSize;
    this.syncUrlState();
    this.load();
  }

  isOrderable(column: DataTableColumn): boolean {
    const index = (this.options?.columns || []).indexOf(column);
    const definition = this.defsFor(index).find((item) => item['orderable'] !== undefined);
    return this.options?.ordering !== false && (column.orderable ?? definition?.['orderable']) !== false;
  }

  sortDirection(index: number): SortDirection | null {
    if (!this.isOrderable(this.columns[index])) return null;
    const entry = this.order.find(([column]) => column === this.originalIndex(index));
    return entry ? entry[1] : null;
  }

  sortBy(index: number) {
    const column = this.columns[index];
    if (!this.isOrderable(column)) return;

    const original = this.originalIndex(index);
    const sequence = column.orderSequence?.length ? column.orderSequence : (['asc', 'desc'] as SortDirection[]);
    const current = this.order.length === 1 && this.order[0][0] === original ? this.order[0][1] : null;
    const next = current ? sequence[(sequence.indexOf(current) + 1) % sequence.length] : sequence[0];
    this.order = [[original, next]];
    this.page = 1;
    this.syncUrlState();
    this.load();
  }

  cell(row: any, rowIndex: number, column: DataTableColumn, columnIndex: number): any {
    const value = column.data === null ? row : this.valueOf(row, column.data);
    const content = column.render ? column.render(value, 'display', row, { row: rowIndex, col: this.originalIndex(columnIndex) }) : value;
    const result = content === null || content === undefined || content === '' ? (column.defaultContent ?? '') : content;
    return this.groupActions(column, result);
  }

  private groupActions(column: DataTableColumn, content: any): any {
    if (typeof content !== 'string' || !/^a[cç][oõ]es$/i.test(column.title.trim()) || content.includes('ss-cell-actions')) {
      return content;
    }
    if (!/class="ss-(icon-button|btn)[ "]/.test(content)) {
      return content;
    }
    return `<span class="ss-cell-actions">${content.replace(/>\s+</g, '><').trim()}</span>`;
  }

  width(column: DataTableColumn): string | null {
    if (column.width === undefined || column.width === null || column.width === '') return null;
    return /^\d+$/.test(String(column.width)) ? `${column.width}px` : String(column.width);
  }

  private get urlColumns(): DataTableColumn[] {
    return (this.options?.columns || []).filter((column) =>
      column.urlKey && /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/.test(column.urlKey) && this.isOrderable(column));
  }

  private get defaultOrder(): [number, SortDirection][] {
    return this.options?.ordering === false ? [] : this.options?.order ?? [[0, 'asc']];
  }

  private urlDefinitions(): UrlParamDefinition[] {
    const [defaultSort] = this.defaultOrder;
    const defaultColumn = defaultSort ? this.options.columns[defaultSort[0]] : undefined;
    const [activeSort] = this.order;
    const direction = activeSort && defaultSort && activeSort[0] === defaultSort[0] ? defaultSort[1] : 'asc';

    return [
      { key: 'pagina', type: 'number', minValue: 1, defaultValue: 1 },
      { key: 'por-pagina', type: 'enum', options: ['15', '50', '100'], defaultValue: this.options.pageLength || 15 },
      { key: 'ordenar', type: 'enum', options: this.urlColumns.map((column) => column.urlKey!), defaultValue: this.defaultOrder.length === 1 ? defaultColumn?.urlKey : undefined },
      { key: 'direcao', type: 'enum', options: ['asc', 'desc'], defaultValue: direction }
    ];
  }

  private restoreUrlState(): void {
    if (!this.options?.urlSync) return;

    const values = this.urlState.read(this.urlDefinitions());
    if (this.paging) {
      this.page = values['pagina'] || 1;
      const size = Number(values['por-pagina']);
      if (this.pageSizeOptions.includes(size)) this.pageSize = size;
    }

    const [defaultSort] = this.defaultOrder;
    const column = values['ordenar']
      ? this.urlColumns.find((item) => item.urlKey === values['ordenar'])
      : defaultSort ? this.options.columns[defaultSort[0]] : undefined;

    const discardedOrder = !values['ordenar'] && !!this.urlState.params['ordenar'];

    if (!discardedOrder && column && this.isOrderable(column) && (values['ordenar'] || values['direcao'])) {
      const index = this.options.columns.indexOf(column);
      const direction = defaultSort && index === defaultSort[0] ? defaultSort[1] : 'asc';
      this.order = [[index, values['direcao'] || direction]];
    } else if (values['ordenar'] || this.urlState.params['ordenar']) {
      this.urlState.patch({ 'ordenar': null, 'direcao': null });
    }
  }

  private syncUrlState(): void {
    if (!this.options?.urlSync) return;

    const [sort] = this.order;
    const column = sort ? this.options.columns[sort[0]] : undefined;
    const slug = column && this.urlColumns.includes(column) ? column.urlKey : undefined;
    const isDefaultOrder = JSON.stringify(this.order) === JSON.stringify(this.defaultOrder);

    this.urlState.write(this.urlDefinitions(), {
      'pagina': this.paging ? this.page : null,
      'por-pagina': this.paging ? this.pageSize : null,
      'ordenar': isDefaultOrder ? null : slug,
      'direcao': slug && !isDefaultOrder ? sort[1] : null
    });
  }

  private async load() {
    const version = ++this.loadVersion;
    if (this.options?.urlReady) await this.options.urlReady();
    if (this.destroyed || version !== this.loadVersion) return;
    this.started = true;
    if (this.isServerSide()) {
      this.loadFromServer();
    } else {
      this.loadLocal();
    }
  }

  private isServerSide(): boolean {
    return !!this.options?.ajax;
  }

  private loadFromServer() {
    const request: DataTableRequest = {
      draw: ++this.draw,
      start: this.paging ? (this.page - 1) * this.pageSize : 0,
      length: this.paging ? this.pageSize : -1,
      order: this.options?.ordering === false ? [] : this.order.map(([column, dir]) => ({ column: this.sortColumnIndex(column), dir })),
      columns: (this.options.columns || []).map((column) => ({
        data: column.data,
        name: column.name || '',
        orderable: this.isOrderable(column)
      })),
      search: { value: '', regex: false }
    };
    const draw = request.draw;

    this.loading = true;
    this.options.ajax!(request, (response) => {

      if (draw !== this.draw) return;
      this.loading = false;
      this.rows = response?.data || [];
      this.total = response?.recordsFiltered ?? response?.recordsTotal ?? this.rows.length;
      const lastPage = this.paging ? Math.max(1, Math.ceil(this.total / this.pageSize)) : 1;
      if (this.page > lastPage) {
        this.page = lastPage;
        this.syncUrlState();
        this.loadFromServer();
        return;
      }
      this.loaded.emit(response);
    });
  }

  private loadLocal() {
    const data = [...(this.options?.data || [])];
    const [sort] = this.order;
    const column = sort ? this.options.columns[this.sortColumnIndex(sort[0])] : undefined;

    if (column && this.options?.ordering !== false) {
      const direction = sort[1] === 'desc' ? -1 : 1;
      data.sort((a, b) => this.compare(this.sortValue(a, column), this.sortValue(b, column)) * direction);
    }

    this.total = data.length;
    const lastPage = this.paging ? Math.max(1, Math.ceil(this.total / this.pageSize)) : 1;
    if (this.page > lastPage) {
      this.page = lastPage;
      this.syncUrlState();
    }
    const start = (this.page - 1) * this.pageSize;
    this.rows = this.paging ? data.slice(start, start + this.pageSize) : data;
    this.loaded.emit({ recordsTotal: this.total, data: this.rows });
  }

  private sortValue(row: any, column: DataTableColumn): any {
    const value = column.data === null ? null : this.valueOf(row, column.data);
    if (value !== null && value !== undefined && typeof value !== 'object') return value;

    const rendered = column.render ? column.render(value ?? row, 'sort', row, { row: 0, col: 0 }) : '';
    return String(rendered ?? '').replace(/<[^>]*>/g, '').trim();
  }

  private compare(a: any, b: any): number {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a ?? '').localeCompare(String(b ?? ''), 'pt-BR', { numeric: true, sensitivity: 'base' });
  }

  private valueOf(row: any, path: string): any {
    return path.split('.').reduce((value, key) => (value === null || value === undefined ? value : value[key]), row);
  }

  private originalIndex(visibleIndex: number): number {
    return (this.options.columns || []).indexOf(this.columns[visibleIndex]);
  }
}
